// src/components/telemetry/TrainingPilotPanel.jsx
// =============================================================================
// Pannello di analisi del pilota selezionato (allenamento live e replay).
// Schede:
//   TIMING          giro in corso, delta live stile F1, riferimenti, giri, curve
//   DINAMICA · IMU  cerchio di aderenza, canali accelerometro/giroscopio e
//                   telemetria sul giro a strisce sovrapposte
//   CAN BUS         dati motore dalla rete CAN del veicolo (oggi non ricevuti)
//   DEVICE          stato del dispositivo
// Presentazionale: riceve i ref del genitore (TrainingLive o TrainingDetail).
//   timing   { clock, delta, deltaRef, last, lastFresh } — solo live
//   obdRef   Map mac → dati CAN (null in replay)
// Auto guidata da più piloti: un selettore sotto la testata sceglie il pilota
// di cui vedere numeri, settori, giri e curve (di default chi è alla guida, o
// l'ultimo nel replay). onPilotFilter({ key, stints } | null) avvisa il genitore.
// Usato anche dalla pagina pilota della gara (PilotLive): showLabels=false
// (niente etichette dei giri), showHr=true (battito per giro e traccia Battito).
// pilotKey: il pilota lo sceglie la pagina (riepilogo): niente selettore qui.
// =============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import { DeviceStatusBar } from './TelemetryCharts';
import {
  DeltaReadout, StripChart, FrictionCircle, ChannelMeters,
  fmtLapTime, fmtSigned,
} from './TelemetryPro';
import { stintPilotKey } from '../../utils/crew';
import './TrainingPanel.css';

const TABS = [
  { key: 'timing', label: 'Timing' },
  { key: 'dinamica', label: 'Dinamica · IMU' },
  { key: 'can', label: 'CAN bus' },
  { key: 'device', label: 'Device' },
];

// Canali della telemetria sul giro (colori dati, uguali in entrambi i temi)
const CH = {
  speed: { key: 'speed', label: 'Velocità', unit: 'km/h', color: '--rs-text-0', minSpan: 20, floor: 0, decimals: 0, scaleDec: 0 },
  accelX: { key: 'accelX', label: 'Acc X · lat', unit: 'G', color: '#38bdf8', signed: true, minSpan: 1, decimals: 2 },
  accelY: { key: 'accelY', label: 'Acc Y · long', unit: 'G', color: '#f59e0b', signed: true, minSpan: 1, decimals: 2 },
  accelZ: { key: 'accelZ', label: 'Acc Z · vert', unit: 'G', color: '#a78bfa', minSpan: 1, decimals: 2 },
  totalG: { key: 'totalG', label: 'G combinata', unit: 'G', color: '#e2e8f0', minSpan: 1, floor: 0, decimals: 2 },
  gyroZ: { key: 'gyroZ', label: 'Gyro Z · yaw', unit: '°/s', color: '#facc15', signed: true, minSpan: 40, decimals: 0, scaleDec: 0 },
  gyroX: { key: 'gyroX', label: 'Gyro X · roll', unit: '°/s', color: '#34d399', signed: true, minSpan: 40, decimals: 0, scaleDec: 0 },
  gyroY: { key: 'gyroY', label: 'Gyro Y · pitch', unit: '°/s', color: '#f472b6', signed: true, minSpan: 40, decimals: 0, scaleDec: 0 },
  hr: { key: 'hr', label: 'Battito', unit: 'bpm', color: '#ff2d55', minSpan: 20, decimals: 0, scaleDec: 0 },
};
const CHANNEL_SETS = {
  tutti: [CH.speed, CH.accelX, CH.accelY, CH.accelZ, CH.gyroZ, CH.gyroX, CH.gyroY],
  acc: [CH.speed, CH.accelX, CH.accelY, CH.accelZ, CH.totalG],
  gyro: [CH.speed, CH.gyroZ, CH.gyroX, CH.gyroY],
  hr: [CH.speed, CH.hr],
};
const METER_GROUPS = [
  {
    title: 'Accelerometro', unit: 'G', channels: [
      { key: 'accelX', label: 'X', axis: 'laterale', color: '#38bdf8', range: 1.5 },
      { key: 'accelY', label: 'Y', axis: 'longitudinale', color: '#f59e0b', range: 1.5 },
      { key: 'accelZ', label: 'Z', axis: 'verticale', color: '#a78bfa', range: 1.5 },
    ],
  },
  {
    title: 'Giroscopio', unit: '°/s', channels: [
      { key: 'gyroX', label: 'X', axis: 'rollio', color: '#34d399', range: 120, decimals: 0 },
      { key: 'gyroY', label: 'Y', axis: 'beccheggio', color: '#f472b6', range: 120, decimals: 0 },
      { key: 'gyroZ', label: 'Z', axis: 'imbardata', color: '#facc15', range: 120, decimals: 0 },
    ],
  },
];

// Canali previsti dalla rete CAN del veicolo
const CAN_CHANNELS = [
  { key: 'rpm', label: 'Regime motore', unit: 'rpm', fmt: v => Math.round(v).toLocaleString('it-IT') },
  { key: 'gear', label: 'Marcia', unit: '', fmt: v => (v === 0 ? 'N' : String(v)) },
  { key: 'throttle', label: 'Acceleratore', unit: '%', fmt: v => Math.round(v) },
  { key: 'brake', label: 'Freno', unit: '%', fmt: v => Math.round(v) },
  { key: 'coolantTemp', label: 'Temperatura acqua', unit: '°C', fmt: v => v.toFixed(0) },
  { key: 'oilTemp', label: 'Temperatura olio', unit: '°C', fmt: v => v.toFixed(0) },
  { key: 'intakeTemp', label: 'Temperatura aspirazione', unit: '°C', fmt: v => v.toFixed(0) },
  { key: 'fuelPct', label: 'Carburante', unit: '%', fmt: v => v.toFixed(1) },
  { key: 'battV', label: 'Tensione batteria', unit: 'V', fmt: v => v.toFixed(1) },
  { key: 'engineLoad', label: 'Carico motore', unit: '%', fmt: v => Math.round(v) },
];

// Campiona i dati CAN dal ref (aggiornato via WS) a 5 Hz, solo con la scheda aperta.
function useCanSample(obdRef, mac, enabled) {
  const [obd, setObd] = useState(null);
  useEffect(() => {
    if (!enabled || !mac) { setObd(null); return undefined; }
    let t;
    const tick = () => {
      const live = obdRef?.current?.get ? obdRef.current.get(mac) : null;
      setObd(live && !live.sim ? live : null);
      t = setTimeout(tick, 200);
    };
    tick();
    return () => clearTimeout(t);
  }, [obdRef, mac, enabled]);
  return obd;
}

const Section = ({ title, aside, children, className = '' }) => (
  <section className={`tp-sec ${className}`}>
    <header className="tp-sec__head">
      <h3 className="tp-sec__title">{title}</h3>
      {aside && <div className="tp-sec__aside">{aside}</div>}
    </header>
    {children}
  </section>
);

const Segmented = ({ value, onChange, options, label }) => (
  <div className="tp-seg" role="group" aria-label={label}>
    {options.map(o => (
      <button key={o.value} type="button" className={value === o.value ? 'is-active' : ''} onClick={() => onChange(o.value)}>{o.label}</button>
    ))}
  </div>
);

export default function TrainingPilotPanel({
  driver,
  lapHistory = [],
  compareLapNum,
  onChangeCompareLap,
  lapDataRef,
  telemetryBufferRef,
  compareLapRef,
  hoverRef,
  brandColor = '#C0FF03',
  replay = false,
  ggStatic = false,
  obdRef = null,
  canLabel = false,
  onLabelLap = null,
  instructorLapNum = null,
  instructorTrailMode = 'blue',
  onSetInstructorLap = null,
  timing = null,
  focusLapNum = null,
  onPilotFilter = null,
  showLabels = true,
  showHr = false,
  pilotKey = undefined,
}) {
  const [tab, setTab] = useState('timing');
  const [editingLap, setEditingLap] = useState(null);
  const [draftLabel, setDraftLabel] = useState('');
  const [savingLabel, setSavingLabel] = useState(false);
  const [labelError, setLabelError] = useState('');
  const [channelSet, setChannelSet] = useState('tutti');
  const [filtered, setFiltered] = useState(true);

  const mac = driver?.mac || null;
  const can = useCanSample(obdRef, mac, tab === 'can' && !replay);
  const lapLabels = useMemo(() => driver?.lapLabels || {}, [driver]);
  const channels = CHANNEL_SETS[channelSet] || CHANNEL_SETS.tutti;

  // ── Auto con più piloti: pilota da analizzare ──
  const [pilotPick, setPilotPick] = useState(null);   // null = chi è alla guida (o l'ultimo)
  useEffect(() => { setPilotPick(null); }, [mac]);
  const crew = useMemo(() => {
    const stints = Array.isArray(driver?.stints) ? driver.stints : [];
    const keyOf = stintPilotKey;
    const pilots = [];
    for (const s of stints) {
      const k = keyOf(s);
      let p = pilots.find(x => x.key === k);
      if (!p) { p = { key: k, name: s.pilot?.name || 'Pilota', ns: new Set(), stints: [] }; pilots.push(p); }
      p.ns.add(Number(s.n));
      p.stints.push(s);
    }
    const lastWithLaps = [...stints].reverse().find(s => Number(s.laps) > 0);
    return {
      stints, pilots,
      currentKey: stints.length ? keyOf(stints[stints.length - 1]) : null,
      lastActiveKey: lastWithLaps ? keyOf(lastWithLaps) : (stints.length ? keyOf(stints[stints.length - 1]) : null),
    };
  }, [driver?.stints]);
  const multiPilot = crew.pilots.length > 1;
  const controlled = pilotKey !== undefined;
  // di default: dal vivo chi è alla guida, nel replay l'ultimo pilota con giri
  const selKey = controlled ? (crew.pilots.some(p => p.key === pilotKey) ? pilotKey : 'all')
    : !multiPilot ? 'all'
      : (pilotPick === 'all' || crew.pilots.some(p => p.key === pilotPick)) ? pilotPick
        : (replay ? crew.lastActiveKey : crew.currentKey);
  const selPilot = crew.pilots.find(p => p.key === selKey) || null;   // null = tutti i piloti
  const filterSig = selPilot ? `${selPilot.key}|${[...selPilot.ns].join(',')}` : '';
  useEffect(() => {
    if (!onPilotFilter) return;
    if (!filterSig) { onPilotFilter(null); return; }
    const cut = filterSig.lastIndexOf('|');
    onPilotFilter({ key: filterSig.slice(0, cut), stints: filterSig.slice(cut + 1).split(',').map(Number) });
  }, [filterSig, onPilotFilter]);

  if (!driver) {
    return <div className="tp-empty">Seleziona un pilota per vedere la telemetria.</div>;
  }

  const startEdit = (lapNum) => { setEditingLap(lapNum); setDraftLabel(lapLabels[lapNum] || ''); setLabelError(''); };
  const cancelEdit = () => { setEditingLap(null); setDraftLabel(''); setLabelError(''); };
  const saveEdit = async () => {
    if (!onLabelLap || editingLap == null || savingLabel) return;
    setSavingLabel(true);
    setLabelError('');
    const ok = await onLabelLap(editingLap, draftLabel.trim());
    setSavingLabel(false);
    if (ok) cancelEdit();
    else setLabelError('Salvataggio non riuscito, riprova.');
  };

  // Turno e pilota di ogni giro (auto con i cambi pilota)
  const stintList = crew.stints;
  const stintOfLap = (l) => l.stint ?? stintList.find(s => s.lapFrom != null && Number(l.lapNum) >= s.lapFrom && Number(l.lapNum) <= s.lapTo)?.n;
  const pilotOfLap = (l) => { const n = stintOfLap(l); return stintList.find(s => s.n === n)?.pilot?.name || null; };
  const currentName = crew.pilots.find(p => p.key === crew.currentKey)?.name || '';
  // il pilota scelto è quello in pista adesso? (i dati "dal vivo" sono suoi)
  const isCurrentSel = !selPilot || (!replay && selPilot.key === crew.currentKey);
  const laps = selPilot ? lapHistory.filter(l => selPilot.ns.has(Number(stintOfLap(l)))) : lapHistory;
  const completedAll = lapHistory.filter(l => !l.partial && l.lapTime);
  const completed = laps.filter(l => !l.partial && l.lapTime);

  // Numeri del pilota scelto: dai turni (contano anche i giri oltre quelli in
  // memoria); tempi per curva dai turni o, se mancano, dai giri a disposizione.
  const own = selPilot ? (() => {
    const st = selPilot.stints;
    const bests = st.map(x => Number(x.bestLapTime)).filter(v => Number.isFinite(v) && v > 0);
    const lastSt = [...st].reverse().find(x => Number(x.laps) > 0);
    const minInto = (acc, obj) => {
      Object.entries(obj || {}).forEach(([k, v]) => {
        if (!Number.isFinite(v) || v <= 0) return;
        if (acc[k] == null || v < acc[k]) acc[k] = v;
      });
      return acc;
    };
    let bestCorners = st.reduce((acc, x) => minInto(acc, x.bestCornerTimes), {});
    if (!Object.keys(bestCorners).length) bestCorners = completed.reduce((acc, l) => minInto(acc, l.cornerTimes), {});
    return {
      laps: st.reduce((a, x) => a + (Number(x.laps) || 0), 0),
      best: bests.length ? Math.min(...bests) : null,
      last: lastSt?.lastLapTime ?? (completed.length ? completed[completed.length - 1].lapTime : null),
      bestCorners,
      lastCorners: lastSt?.lastCornerTimes || [...completed].reverse().find(l => l.cornerTimes)?.cornerTimes || null,
    };
  })() : null;

  const best = own ? own.best : driver.bestLapTime;
  const lastLap = own ? own.last : driver.lastLapTime;
  const lapsCount = own ? own.laps : driver.lapCount;
  const lastMinusBest = lastLap && best ? lastLap - best : null;
  const instructorLap = lapHistory.find(l => Number(l.lapNum) === Number(instructorLapNum)) || null;
  const cornerNames = Object.keys(driver.bestCornerTimes || driver.cornerTimes || {});
  const cornerLast = (name) => (own && !(isCurrentSel && !replay) ? own.lastCorners?.[name] : driver.cornerTimes?.[name]);
  const cornerBest = (name) => (own ? own.bestCorners?.[name] : driver.bestCornerTimes?.[name]);
  const whose = selPilot ? `di ${selPilot.name}` : 'della sessione';

  // ── Settori (S1, S2, S3): dal giro salvato o dallo storico del pilota ──
  const SK = ['S1', 'S2', 'S3'];
  const secNum = (x) => (Number.isFinite(Number(x)) && Number(x) > 0 ? Number(x) : null);
  const sectorsOf = (l) => {
    const src = l?.sectors || (Array.isArray(driver.sectorHistory) ? driver.sectorHistory[Number(l?.lapNum) - 1] : null);
    return src ? { S1: secNum(src.S1), S2: secNum(src.S2), S3: secNum(src.S3) } : null;
  };
  const lapSectors = new Map(completed.map(l => [Number(l.lapNum), sectorsOf(l)]));
  const secBest = {};
  for (const k of SK) {
    const vals = [...lapSectors.values()].map(x => x?.[k]).filter(Number.isFinite).sort((a, b) => a - b);
    const med = vals.length ? vals[vals.length >> 1] : null;
    // tempi impossibili (cancello saltato) fuori dal migliore
    const ok = vals.filter(v => !med || (v > med * 0.6 && v < med * 1.6));
    secBest[k] = ok.length ? ok[0] : null;
  }
  const hasSectors = SK.some(k => secBest[k] != null);
  const idealLap = SK.every(k => secBest[k] != null) ? SK.reduce((a, k) => a + secBest[k], 0) : null;
  const isSecBest = (k, v) => v != null && secBest[k] != null && Math.abs(v - secBest[k]) < 0.0005;
  const fmtSec = (v) => (v == null ? '—' : v >= 60 ? fmtLapTime(v) : v.toFixed(3));
  // valori mostrati nel riquadro: giro in corso (live) o giro analizzato (replay)
  const lastLapNum = completed.length ? Math.max(...completed.map(l => Number(l.lapNum))) : null;
  const lastSec = (replay || !isCurrentSel ? null : (driver.lastSectorTimes && SK.some(k => secNum(driver.lastSectorTimes[k])) ? driver.lastSectorTimes : null)) || lapSectors.get(lastLapNum) || null;
  const curSec = replay || !isCurrentSel ? null : (driver.sectorTimes || null);

  // Replay: delta del giro analizzato rispetto al giro di confronto
  const focusLap = replay ? lapHistory.find(l => Number(l.lapNum) === Number(focusLapNum ?? lapDataRef?.current?.currentLapNum)) : null;
  const compareLap = compareLapNum != null ? lapHistory.find(l => Number(l.lapNum) === Number(compareLapNum)) : null;
  const replayDelta = focusLap?.lapTime && compareLap?.lapTime ? focusLap.lapTime - compareLap.lapTime : null;

  const lapOption = (l) => {
    const lbl = lapLabels[l.lapNum] ? ` · ${lapLabels[l.lapNum]}` : '';
    return `L${l.lapNum}${l.partial ? ' (parziale)' : ''} · ${l.lapTime ? fmtLapTime(l.lapTime) : '—'}${lbl}`;
  };

  const canStatus = can ? 'live' : 'off';

  const lapHrAvg = (l) => {
    if (Number.isFinite(l?.hrAvg) && l.hrAvg > 0) return Math.round(l.hrAvg);
    const v = (Array.isArray(l?.hr) ? l.hr : []).map(p => p?.value).filter(x => Number.isFinite(x) && x > 0);
    return v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : null;
  };
  const lapsTotal = crew.pilots.reduce((a, p) => a + p.stints.reduce((b, x) => b + (Number(x.laps) || 0), 0), 0);
  const crewMeta = driver.car && !controlled
    ? (replay
      ? `Piloti: ${[...new Set(stintList.map(s => s.pilot?.name).filter(Boolean))].join(', ') || '—'}`
      : `Alla guida: ${driver.pilot?.name || '—'}`)
    : null;

  return (
    <section className="tp-panel" style={{ '--tp-driver': brandColor }}>
      {/* ── Identità + numeri chiave ── */}
      <header className="tp-head">
        <div className="tp-id">
          <span className="tp-id__swatch" />
          <div className="tp-id__text">
            <div className="tp-id__name">{driver.fullName || driver.tag || driver.mac}</div>
            <div className="tp-id__meta">
              {[crewMeta, driver.team, driver.formula?.label, driver.tag, mac].filter(Boolean).join(' · ')}
            </div>
          </div>
        </div>
        <dl className="tp-kpis">
          <div><dt>Giri</dt><dd>{lapsCount ?? 0}</dd></div>
          <div><dt>Ultimo</dt><dd>{fmtLapTime(lastLap)}</dd></div>
          <div className="is-best"><dt>Best</dt><dd>{fmtLapTime(best)}</dd></div>
          <div><dt>Ultimo − best</dt><dd className={lastMinusBest > 0.0005 ? 'is-slower' : lastMinusBest != null ? 'is-even' : ''}>{lastMinusBest == null ? '—' : fmtSigned(lastMinusBest)}</dd></div>
        </dl>
      </header>

      {/* ── Auto con più piloti: di chi sono tempi, settori, giri e curve ── */}
      {multiPilot && !controlled && (
        <div className="tp-pilots" role="group" aria-label="Pilota da analizzare">
          <span className="tp-pilots__label">Pilota</span>
          <div className="tp-pilots__list">
            {crew.pilots.map(p => {
              const n = p.stints.reduce((a, x) => a + (Number(x.laps) || 0), 0);
              const live = !replay && p.key === crew.currentKey;
              return (
                <button key={p.key} type="button" className={`tp-pilot${selKey === p.key ? ' is-active' : ''}${live ? ' is-live' : ''}`}
                  aria-pressed={selKey === p.key} onClick={() => setPilotPick(live ? null : p.key)}
                  title={live ? `${p.name} è alla guida: la scheda segue chi guida` : `Tempi, settori, giri e curve di ${p.name}`}>
                  <strong>{p.name}</strong>
                  <span>{live ? 'alla guida · ' : ''}{n} {n === 1 ? 'giro' : 'giri'}</span>
                </button>
              );
            })}
            <button type="button" className={`tp-pilot tp-pilot--all${selKey === 'all' ? ' is-active' : ''}`}
              aria-pressed={selKey === 'all'} onClick={() => setPilotPick('all')} title="Tutti i giri dell'auto insieme">
              <strong>Tutti</strong>
              <span>{lapsTotal} {lapsTotal === 1 ? 'giro' : 'giri'}</span>
            </button>
          </div>
        </div>
      )}

      {/* ── Schede ── */}
      <nav className="tp-tabs" role="tablist" aria-label="Aree di analisi">
        {TABS.map(t => (
          <button
            key={t.key}
            role="tab"
            type="button"
            data-tab={t.key}
            aria-selected={tab === t.key}
            className={`tp-tab${tab === t.key ? ' is-active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === 'can' && <span className={`tp-tab__state is-${canStatus}`}>{can ? 'Live' : 'No data'}</span>}
          </button>
        ))}
      </nav>

      {/* ═══════════════ TIMING ═══════════════ */}
      {tab === 'timing' && (
        <div className="tp-body">
          {!replay ? (
            <div className="tp-live">
              <div className="tp-live__cell tp-live__clock">
                <span className="tp-label">Giro in corso{driver.lapCount != null ? ` · L${(driver.lapCount || 0) + 1}` : ''}{!isCurrentSel && currentName ? ` · ${currentName}` : ''}</span>
                <strong>{timing?.clock == null ? '—' : fmtLapTime(timing.clock, 1)}</strong>
                {timing?.clock == null && <span className="tp-live__sub">In attesa del passaggio sul traguardo</span>}
                {Number.isFinite(Number(driver.speedKmh)) && <span className="tp-live__speed"><b>{Math.round(Number(driver.speedKmh))}</b> km/h</span>}
              </div>
              <div className="tp-live__cell tp-live__delta">
                <DeltaReadout
                  size="xl"
                  value={timing?.delta ?? null}
                  refInfo={timing?.deltaRef || null}
                  last={timing?.last || null}
                  lastFresh={!!timing?.lastFresh}
                  note={completedAll.length ? 'Delta disponibile quando la vettura è in pista' : 'Il delta parte dopo il primo giro completo'}
                />
              </div>
              <div className="tp-live__cell tp-live__laps">
                <div><span className="tp-label">Ultimo giro{selPilot && !isCurrentSel ? ` · ${selPilot.name}` : ''}</span><strong>{fmtLapTime(lastLap)}</strong>
                  {timing?.last && isCurrentSel && <em className={timing.last.value < 0 ? 'is-faster' : timing.last.value > 0 ? 'is-slower' : ''}>{fmtSigned(timing.last.value)} vs L{timing.last.refLap}</em>}
                </div>
                <div><span className="tp-label">Best</span><strong className="is-best">{fmtLapTime(best)}</strong></div>
              </div>
            </div>
          ) : (
            <div className="tp-live">
              <div className="tp-live__cell tp-live__clock">
                <span className="tp-label">Giro analizzato · L{focusLap?.lapNum ?? '—'}</span>
                <strong>{fmtLapTime(focusLap?.lapTime)}</strong>
                <span className="tp-live__sub">{focusLap?.totalDist ? `${Math.round(focusLap.totalDist)} m` : '—'}</span>
              </div>
              <div className="tp-live__cell tp-live__delta">
                <DeltaReadout
                  size="xl"
                  value={replayDelta}
                  refInfo={compareLap ? { lap: compareLap.lapNum, kind: Number(compareLap.lapNum) === Number(instructorLapNum) ? 'instructor' : 'compare', lapTime: compareLap.lapTime } : null}
                  note="Scegli un giro di confronto"
                />
              </div>
              <div className="tp-live__cell tp-live__laps">
                <div><span className="tp-label">{selPilot ? 'Best del pilota' : 'Best sessione'}</span><strong className="is-best">{fmtLapTime(best)}</strong></div>
                <div><span className="tp-label">Giri completi</span><strong>{completed.length}</strong></div>
              </div>
            </div>
          )}

          {!replay && (
          <div className="tp-refbar">
            <label className="tp-refbar__field">
              <span className="tp-label">Confronto grafici</span>
              <select value={compareLapNum == null ? 'auto' : String(compareLapNum)} onChange={(e) => onChangeCompareLap(e.target.value === 'auto' ? null : Number(e.target.value))}>
                <option value="auto">Giro precedente</option>
                {(compareLap && !laps.includes(compareLap) ? [...laps, compareLap] : laps).map(l => <option key={l.lapNum} value={l.lapNum}>{lapOption(l)}</option>)}
              </select>
            </label>
            {onSetInstructorLap && (
              <>
                <label className="tp-refbar__field">
                  <span className="tp-label">Giro istruttore · riferimento del delta</span>
                  <select value={instructorLapNum ?? ''} onChange={e => onSetInstructorLap(e.target.value === '' ? null : Number(e.target.value), instructorTrailMode)}>
                    <option value="">Nessuno · si usa il best</option>
                    {completedAll.map(l => <option key={l.lapNum} value={l.lapNum}>L{l.lapNum} · {fmtLapTime(l.lapTime)}{multiPilot && pilotOfLap(l) ? ` · ${pilotOfLap(l)}` : ''}</option>)}
                  </select>
                </label>
                <div className="tp-refbar__field">
                  <span className="tp-label">Traiettoria riferimento</span>
                  <Segmented
                    label="Traiettoria del riferimento sulla mappa"
                    value={instructorTrailMode}
                    onChange={(v) => instructorLapNum && onSetInstructorLap(instructorLapNum, v)}
                    options={[{ value: 'blue', label: 'Azzurra' }, { value: 'speed', label: 'Velocità' }]}
                  />
                </div>
              </>
            )}
          </div>
          )}

          {hasSectors && (
            <Section title="Settori" aside={<span className="tp-sec__hint">{replay ? `Giro L${focusLap?.lapNum ?? '—'} e migliori ${whose}` : `${curSec ? 'Giro in corso, ultimo giro' : 'Ultimo giro'} e migliori ${whose}`} · in viola il migliore</span>}>
              <div className="tp-sectors">
                {SK.map(k => {
                  const live = secNum(curSec?.[k]);
                  const shown = replay ? sectorsOf(focusLap)?.[k] ?? null : (live ?? null);
                  const last = replay ? null : secNum(lastSec?.[k]);
                  const ref = shown ?? last;
                  const d = ref != null && secBest[k] != null ? ref - secBest[k] : null;
                  return (
                    <div key={k} className={`tp-sector ${isSecBest(k, ref) ? 'is-pb' : ''}`}>
                      <div className="tp-sector__k">{k}{!replay && <span>{shown != null ? 'giro in corso' : last != null ? 'ultimo giro' : ''}</span>}</div>
                      <strong className={isSecBest(k, ref) ? 'is-best' : ''}>{fmtSec(ref)}</strong>
                      <div className="tp-sector__foot">
                        <span className={d == null ? '' : d > 0.0005 ? 'is-slower' : 'is-best'}>{d == null ? '' : d > 0.0005 ? fmtSigned(d) : 'migliore'}</span>
                        <span>best <b>{fmtSec(secBest[k])}</b></span>
                      </div>
                    </div>
                  );
                })}
                <div className="tp-sector tp-sector--ideal">
                  <div className="tp-sector__k">Giro ideale<span>somma dei migliori</span></div>
                  <strong>{idealLap != null ? fmtLapTime(idealLap) : '—'}</strong>
                  <div className="tp-sector__foot">
                    <span>{idealLap != null && best ? `${fmtSigned(idealLap - best)} sul best` : ''}</span>
                  </div>
                </div>
              </div>
            </Section>
          )}

          <Section title="Giri" aside={<span className="tp-count">{laps.length}</span>}>
            {laps.length === 0 ? (
              <div className="tp-note">Nessun giro completato.</div>
            ) : (
              <div className="tp-table-wrap tp-table-wrap--laps">
                <table className="tp-table">
                  <thead>
                    <tr>
                      <th>Giro</th>
                      {multiPilot && !selPilot && <th>Pilota</th>}
                      <th className="num">Tempo</th>
                      <th className="num">Δ best</th>
                      {instructorLap && <th className="num">Δ istruttore</th>}
                      {hasSectors && SK.map(k => <th key={k} className="num">{k}</th>)}
                      <th className="num">Distanza</th>
                      {showHr && <th className="num" title="Battito medio del giro (bpm)">FC</th>}
                      {showLabels && <th>Etichetta</th>}
                      {showLabels && canLabel && <th aria-label="Azioni" />}
                    </tr>
                  </thead>
                  <tbody>
                    {[...laps].reverse().map(l => {
                      const isBest = best && l.lapTime && Math.abs(l.lapTime - best) < 0.0005;
                      const isRef = Number(l.lapNum) === Number(instructorLapNum);
                      const dBest = l.lapTime && best ? l.lapTime - best : null;
                      const dIns = l.lapTime && instructorLap?.lapTime ? l.lapTime - instructorLap.lapTime : null;
                      const isEditing = editingLap === l.lapNum;
                      return (
                        <tr key={l.lapNum} className={`${isBest ? 'is-best ' : ''}${isRef ? 'is-ref' : ''}`}>
                          <td className="tp-mono">
                            L{l.lapNum}{l.partial ? '*' : ''}
                            {isRef && <span className="tp-tag tp-tag--ref">Istruttore</span>}
                            {isBest && <span className="tp-tag tp-tag--best">Best</span>}
                            {l.swap && <span className="tp-tag" title="Durante questo giro c'è stato il cambio pilota">Cambio</span>}
                          </td>
                          {multiPilot && !selPilot && <td className="tp-who">{pilotOfLap(l) || '—'}</td>}
                          <td className="num tp-mono tp-strong">{l.lapTime ? fmtLapTime(l.lapTime) : '—'}</td>
                          <td className={`num tp-mono ${dBest > 0.0005 ? 'is-slower' : ''}`}>{dBest == null ? '—' : fmtSigned(dBest)}</td>
                          {instructorLap && <td className={`num tp-mono ${dIns < -0.0005 ? 'is-faster' : dIns > 0.0005 ? 'is-slower' : ''}`}>{dIns == null || isRef ? '—' : fmtSigned(dIns)}</td>}
                          {hasSectors && SK.map(k => {
                            const v = l.partial ? null : lapSectors.get(Number(l.lapNum))?.[k] ?? null;
                            return <td key={k} className={`num tp-mono ${isSecBest(k, v) ? 'is-best' : 'tp-dim'}`}>{fmtSec(v)}</td>;
                          })}
                          <td className="num tp-mono tp-dim">{l.totalDist ? `${Math.round(l.totalDist)} m` : '—'}</td>
                          {showHr && <td className="num tp-mono">{lapHrAvg(l) ?? '—'}</td>}
                          {showLabels && <td className="tp-label-cell">
                            {isEditing ? (
                              <span className="tp-label-edit">
                                <input
                                  autoFocus
                                  value={draftLabel}
                                  maxLength={40}
                                  placeholder="es. Warm up, Prova gomme"
                                  onChange={e => setDraftLabel(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                                  disabled={savingLabel}
                                />
                                <button type="button" className="tp-mini-btn is-ok" onClick={saveEdit} disabled={savingLabel}>Salva</button>
                                <button type="button" className="tp-mini-btn" onClick={cancelEdit} disabled={savingLabel}>Annulla</button>
                              </span>
                            ) : lapLabels[l.lapNum] ? (
                              <span className="tp-lap-label" title={lapLabels[l.lapNum]}>{lapLabels[l.lapNum]}</span>
                            ) : <span className="tp-dim">—</span>}
                            {isEditing && labelError && <div className="tp-label-error">{labelError}</div>}
                          </td>}
                          {showLabels && canLabel && (
                            <td className="tp-actions">
                              {!isEditing && (
                                <button type="button" className="tp-icon-btn" onClick={() => startEdit(l.lapNum)} title={lapLabels[l.lapNum] ? 'Modifica etichetta' : 'Dai un nome a questo giro'} aria-label="Etichetta giro">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /></svg>
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {laps.some(l => l.partial) && <div className="tp-note">* giro parziale, in corso al termine della sessione</div>}
                {showLabels && canLabel && <div className="tp-note">Con la matita dai un nome ai giri («Warm up», «Prova gomme»): l'allievo lo vede come riferimento.</div>}
              </div>
            )}
          </Section>

          {cornerNames.length > 0 && (
            <Section title="Tempi per curva" aside={<span className="tp-sec__hint">Ultimo passaggio e migliore {whose}{instructorLap ? ' · rif. istruttore' : ''}</span>}>
              <div className="tp-table-wrap">
                <table className="tp-table">
                  <thead><tr><th>Curva</th><th className="num">Ultimo</th><th className="num">{instructorLap ? 'Istruttore' : 'Best'}</th><th className="num">Δ</th></tr></thead>
                  <tbody>
                    {cornerNames.map(name => {
                      const last = cornerLast(name);
                      const ref = instructorLap?.cornerTimes?.[name] ?? cornerBest(name);
                      const d = last != null && ref != null ? last - ref : null;
                      return (
                        <tr key={name}>
                          <td>{name}</td>
                          <td className="num tp-mono">{last != null ? `${last.toFixed(3)}` : '—'}</td>
                          <td className="num tp-mono tp-dim">{ref != null ? `${ref.toFixed(3)}` : '—'}</td>
                          <td className={`num tp-mono ${d < -0.0005 ? 'is-faster' : d > 0.0005 ? 'is-slower' : ''}`}>{d == null ? '—' : fmtSigned(d)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Section>
          )}
        </div>
      )}

      {/* ═══════════════ DINAMICA · IMU ═══════════════ */}
      {tab === 'dinamica' && (
        <div className="tp-body">
          <div className="tp-imu">
            <Section title="Cerchio di aderenza" aside={<span className="tp-sec__hint">{ggStatic ? 'Tutto il giro' : 'Ultimi 10 s'}</span>} className="tp-imu__gg">
              <FrictionCircle bufferRef={telemetryBufferRef} accent={brandColor} staticMode={ggStatic} />
            </Section>
            <Section
              title="Canali IMU"
              aside={<span className="tp-sec__hint">{replay ? 'Valori al cursore · minimo / massimo del giro' : 'Valore istantaneo · minimo / massimo del giro in corso'}</span>}
              className="tp-imu__meters"
            >
              <div className="tp-speed">
                <span className="tp-label">Velocità GPS</span>
                <strong>{replay ? '—' : Math.round(driver.speedKmh || 0)}</strong>
                <em>km/h</em>
              </div>
              <ChannelMeters lapDataRef={lapDataRef} hoverRef={hoverRef} groups={METER_GROUPS} live={!replay} />
            </Section>
          </div>

          <Section
            title="Telemetria sul giro"
            className="tp-sec--flush"
            aside={(
              <div className="tp-tools">
                <Segmented label="Canali" value={channelSet} onChange={setChannelSet} options={[{ value: 'tutti', label: 'Tutti' }, { value: 'acc', label: 'Accelerometro' }, { value: 'gyro', label: 'Giroscopio' }, ...(showHr ? [{ value: 'hr', label: 'Battito' }] : [])]} />
                <Segmented label="Filtro" value={filtered ? 'on' : 'off'} onChange={(v) => setFiltered(v === 'on')} options={[{ value: 'off', label: 'Grezzo' }, { value: 'on', label: 'Filtrato' }]} />
              </div>
            )}
          >
            <StripChart
              lapDataRef={lapDataRef}
              compareLapRef={compareLapRef}
              hoverRef={hoverRef}
              channels={channels}
              filterWin={filtered ? 7 : 1}
              live={!replay}
              accent={brandColor}
            />
            <div className="tp-note tp-note--pad">
              Asse orizzontale: distanza dal traguardo. Traccia piena = {replay ? 'giro analizzato' : 'giro in corso'}, tratteggiata = giro di confronto.
              Passa sopra il grafico per leggere i valori e il Δt nel punto.
            </div>
          </Section>
        </div>
      )}

      {/* ═══════════════ CAN BUS ═══════════════ */}
      {tab === 'can' && (
        <div className="tp-body">
          <div className={`tp-can is-${canStatus}`}>
            <div className="tp-can__status">
              <span className="tp-can__led" aria-hidden="true" />
              <div>
                <strong>{can ? 'Dati CAN ricevuti' : 'Dati CAN non ricevuti'}</strong>
                <p>
                  {can
                    ? 'Il dispositivo sta leggendo la rete CAN del veicolo.'
                    : 'Il dispositivo non riceve dati dalla rete CAN del veicolo. Regime, pedali e temperature compariranno qui quando il collegamento CAN sarà attivo.'}
                </p>
              </div>
              <dl className="tp-can__meta">
                <div><dt>Interfaccia</dt><dd>CAN bus</dd></div>
                <div><dt>Stato</dt><dd>{can ? 'Attivo' : 'Nessun segnale'}</dd></div>
              </dl>
            </div>
            <div className="tp-can__grid">
              {CAN_CHANNELS.map(c => {
                const v = can?.[c.key];
                const has = v != null && Number.isFinite(Number(v));
                return (
                  <div key={c.key} className={`tp-can__ch${has ? ' has-data' : ''}`}>
                    <span className="tp-label">{c.label}</span>
                    <strong>{has ? c.fmt(Number(v)) : '—'}{has && c.unit ? <em>{c.unit}</em> : null}</strong>
                    {!has && <small>non ricevuto</small>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ DEVICE ═══════════════ */}
      {tab === 'device' && (
        <div className="tp-body">
          <div className="tp-device">
            <Section title="Stato dispositivo">
              <DeviceStatusBar
                battery={driver.battery} signal4g={driver.signal4g} sats={driver.sats}
                mcuTemp={driver.mcuTemp} modemTemp={driver.modemTemp}
                hr={driver.hr ?? null} beaconId={driver.beaconId ?? null} hrCaption={driver.hrCaption}
              />
            </Section>
            <Section title="Collegamento">
              <dl className="tp-dl">
                <div><dt>MAC</dt><dd className="tp-mono">{mac || '—'}</dd></div>
                <div><dt>Satelliti GPS</dt><dd className={`tp-mono ${driver.sats != null && driver.sats < 5 ? 'is-slower' : ''}`}>{driver.sats ?? '—'}</dd></div>
                <div><dt>Segnale</dt><dd className={`tp-mono ${driver.drPhase && driver.drPhase !== 'LIVE' ? 'is-slower' : ''}`}>{replay ? '—' : (driver.drPhase || 'LIVE')}</dd></div>
              </dl>
            </Section>
          </div>
        </div>
      )}
    </section>
  );
}
